import React from 'react';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import formatMoney from 'utils/formatters/Money';
import {
  getShipmentDetailUrl,
  getLoad,
  getEquipment,
} from 'utils/formatters/shipments';
import PODButton from './PODButton';
import { isArray } from 'lodash';
import { NO_DATA } from '../../../utils/constants';

const formatLocation = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  const loc = isArray(val) ? val[0] : val;
  return (
    loc.name ||
    loc.street ||
    (loc.city && loc.state ? `${loc.city}, ${loc.state}` : '') ||
    loc.address ||
    ''
  );
};

const VALUE_TYPES = {
  DATE: 'DATE',
  EQUIPMENT: 'EQUIPMENT',
  LINK: 'LINK',
  LOCATION: 'LOCATION',
  LOAD: 'LOAD',
  MONEY: 'MONEY',
  DEFAULT: 'DEFAULT',
};

const FORMATTERS = {
  DATE: (value) => moment(value).format('L'),
  EQUIPMENT: getEquipment,
  LINK: (valueId, displayValue = '') => {
    return (
      <Link to={getShipmentDetailUrl(valueId)} target="_blank">
        {displayValue || valueId}
      </Link>
    );
  },
  LOAD: getLoad,
  LOCATION: formatLocation,
  MONEY: (value, currency) => formatMoney(value, currency),
  DEFAULT: (value) => value,
};

const getValue = (value, type, displayValue = '', currency) => {
  switch (type) {
    case VALUE_TYPES.DATE:
      return value ? FORMATTERS.DATE(value) : '';
    case VALUE_TYPES.EQUIPMENT:
      return value ? FORMATTERS.EQUIPMENT(value) : '';
    case VALUE_TYPES.LINK:
      return value ? FORMATTERS.LINK(value, displayValue) : '';
    case VALUE_TYPES.LOAD:
      return value ? FORMATTERS.LOAD(value) : '';
    case VALUE_TYPES.LOCATION:
      return value ? FORMATTERS.LOCATION(value) : '';
    case VALUE_TYPES.MONEY:
      return value
        ? FORMATTERS.MONEY(value, currency)
        : FORMATTERS.MONEY(0, currency);
    default:
      return FORMATTERS.DEFAULT(value);
  }
};

const JobPODColumn = ({ job }) => {
  return <PODButton pod={job.pod} podUrl={job.podUrl} />;
};

const SHORTHAUL_COLUMNS = [
  {
    title: 'DELIVER_BY',
    field: 'jobDeliverBy',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.DATE);
    },
  },
  {
    title: 'SHIPMENT',
    field: 'shipment_id',
    className: 'whitespace-nowrap',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.LINK, item.shipmentId);
    },
  },
  {
    title: 'ORDER',
    field: 'order_id',
    className: 'whitespace-nowrap',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.DEFAULT);
    },
  },
  {
    title: 'ORIGIN',
    titleize: true,
    field: 'origin',
    className: 'min-w-[160px]',
    value(item) {
      return formatLocation(item[this.field]);
    },
  },
  {
    title: 'DESTINATION',
    titleize: true,
    field: 'destination',
    className: 'min-w-[160px]',
    value(item) {
      return formatLocation(item[this.field]);
    },
  },
  {
    title: 'LOAD',
    field: 'loads',
    className: 'min-w-[160px]',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.LOAD);
    },
  },
  {
    title: 'WEIGHT',
    field: 'weight',
    className: 'text-right',
    value(item) {
      if (item.weight == null) return NO_DATA;
      const formatted = item.weight.toLocaleString();
      const unit = item.weight_units ? ` ${item.weight_units}` : '';
      return `${formatted}${unit}`;
    },
  },
  {
    title: 'DRIVER',
    titleize: true,
    count: 1,
    field: 'driverName',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.DEFAULT);
    },
  },
  {
    title: 'TOTAL',
    field: 'total',
    className: 'text-right font-semibold',
    value(item, total) {
      return getValue(
        total,
        VALUE_TYPES.MONEY,
        '',
        item.lineItems?.[0]?.currency,
      );
    },
  },
  {
    title: 'POD',
    field: 'pod',
    value(item) {
      return <JobPODColumn job={item} />;
    },
  },
  {
    title: 'CLOSED_DATE',
    field: 'jobClosed',
    className: 'whitespace-nowrap',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.DATE);
    },
  },
];

const DRAYAGE_COLUMNS = [
  {
    title: 'DELIVER_BY',
    field: 'jobDeliverBy',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.DATE);
    },
  },
  {
    title: 'SHIPMENT',
    field: 'shipment_id',
    className: 'whitespace-nowrap',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.LINK, item.shipmentId);
    },
  },
  {
    title: 'ORDER',
    field: 'order_id',
    className: 'whitespace-nowrap',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.DEFAULT);
    },
  },
  {
    title: 'ORIGIN',
    titleize: true,
    field: 'origin',
    className: 'min-w-[160px]',
    value(item) {
      return formatLocation(item[this.field]);
    },
  },
  {
    title: 'DESTINATION',
    titleize: true,
    field: 'destination',
    className: 'min-w-[160px]',
    value(item) {
      return formatLocation(item[this.field]);
    },
  },
  {
    title: 'EQUIPMENT',
    field: 'equipments',
    className: 'min-w-[160px]',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.EQUIPMENT);
    },
  },
  {
    title: 'WEIGHT',
    field: 'weight',
    className: 'text-right',
    value(item) {
      if (item.weight == null) return NO_DATA;
      const formatted = item.weight.toLocaleString();
      const unit = item.weight_units ? ` ${item.weight_units}` : '';
      return `${formatted}${unit}`;
    },
  },
  {
    title: 'DRIVER',
    titleize: true,
    count: 1,
    field: 'driverName',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.DEFAULT);
    },
  },
  {
    title: 'TOTAL',
    field: 'total',
    className: 'text-right font-semibold',
    value(item, total) {
      return getValue(
        total,
        VALUE_TYPES.MONEY,
        '',
        item.lineItems?.[0]?.currency,
      );
    },
  },
  {
    title: 'POD',
    field: 'pod',
    value(item) {
      return <JobPODColumn job={item} />;
    },
  },
  {
    title: 'CLOSED_DATE',
    field: 'jobClosed',
    className: 'whitespace-nowrap',
    value(item) {
      return getValue(item[this.field], VALUE_TYPES.DATE);
    },
  },
];

const COLUMNS = {
  shorthaul: SHORTHAUL_COLUMNS,
  drayage: DRAYAGE_COLUMNS,
};

export default COLUMNS;
